import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropzone from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { palette } from 'theme/palette';
import Button from '@mui/material/Button';
import { TextField } from '../../common/components/Inputs/TextField/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Input from '@mui/material/Input';
import { useRSimUpload } from './useRSimUpload';
import { AutoHideSnackbar } from '../../common/Snackbar/AutoHideSnackbar';
import { AlertTitle } from '../../common/Snackbar/AlertTitle';
import Stack from '@mui/material/Stack';
import { adminNavigationAtom } from 'src/atoms/pages/pages';
import { Routes } from 'src/Routes.constants';
import { useTabNavigationSetter } from 'src/common/components/AppLayout/AppNavigation/hooks/useTabNavigationSetter';
import { TabPanelWithErrorHandling } from 'src/common/components/AppLayout/Components/TabPanelWithErrorHandling';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';

const DropAreaBox = styled(Box)({
  position: 'absolute',
  top: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  fontSize: '2rem',
});

const Container = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
});

const BoxStyled = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: palette.grey[100],
  borderRadius: DEFAULT_BORDER_RADIUS,
  maxWidth: theme.spacing(512),
  maxHeight: theme.spacing(187),
  padding: theme.spacing(42, 20, 20, 20),
  boxSizing: 'content-box',
  boxShadow: `0px 0px ${theme.spacing(5)} 0px #00000014, 0px ${theme.spacing(1)} ${theme.spacing(
    2,
  )} 0px #00000014`,
}));

const ButtonBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(12),
}));

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  backgroundColor: 'white',
  minWidth: theme.spacing(300),
  mt: 1,
}));

const ButtonStyled = styled(Button)(({ theme }) => ({
  padding: theme.spacing(10, 16),
}));

const TransparentButtonStyled = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent !important',
  padding: theme.spacing(10, 16),
}));

export const RSimManufacturingFilePageContent = () => {
  useTabNavigationSetter({
    tabsAtom: adminNavigationAtom,
    routeToSet: Routes.rsimManufacturingFile,
  });

  const { t } = useTranslation();
  const [dropAreaDisplayed, setDropAreaDisplayed] = useState(false);

  const {
    selectedFile,
    selectFile,
    uploadFile,
    uploading,
    importId,
    downloadOutput,
    importStatus,
    setImportStatus,
    clear,
  } = useRSimUpload();

  return (
    <>
      <Dropzone
        noClick
        onDrop={(acceptedFiles) => {
          if (selectedFile && (importId || importStatus)) {
            return;
          }
          setDropAreaDisplayed(false);
          selectFile(acceptedFiles[0]);
        }}
        onDragEnter={() => {
          if (selectedFile && (importId || importStatus)) {
            return;
          }
          setDropAreaDisplayed(true);
        }}
        onDragLeave={() => setDropAreaDisplayed(false)}
      >
        {({ getRootProps, getInputProps }) => {
          return (
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                width: '100%',
              }}
              data-testid="dropzone"
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <Container>
                <BoxStyled {...getRootProps()} data-testid="RSimManufacturingFile page">
                  <Typography
                    variant="text2"
                    color="text.secondary"
                    sx={{
                      ml: 16,
                    }}
                  >
                    {t('common.fileName')}
                  </Typography>
                  <TextFieldStyled
                    disabled
                    data-testid="RSimManufacturingFile filename input"
                    sx={{
                      '& fieldset': {
                        ...(importStatus?.messages
                          ? { borderColor: `${palette.error.contrastText} !important` }
                          : {}),
                      },
                      '& input': {
                        ...(selectedFile
                          ? {
                              fillColor: `${palette.text.primary} !important`,
                              // '-webkit-text-fill-color': `${palette.text.primary} !important`,
                              WebkitTextFillColor: `${palette.text.primary} !important`,
                            }
                          : {}),
                      },
                    }}
                    value={
                      selectedFile?.name ||
                      t('admin.rSimManufacturingFile.rSimManufacturingFilename')
                    }
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            sx={{
                              ml: 12,
                            }}
                          >
                            <PublishOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{
                              mr: 12,
                              color: palette.error.contrastText,
                            }}
                          >
                            {!!importStatus?.messages && <ErrorOutlineIcon fontSize="small" />}
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <Box>
                    {importStatus?.messages && (
                      <Typography
                        variant="text4"
                        color="error.contrastText"
                        sx={{
                          ml: 20,
                        }}
                      >
                        {importStatus?.messages[0]}
                      </Typography>
                    )}
                  </Box>
                  <ButtonBox sx={{ marginTop: importStatus?.messages ? '' : 24 }}>
                    {/* ------------- BRAK PLIKU ---------------- */}
                    {!selectedFile && (
                      <>
                        <Typography variant="h5" color="text.secondary">
                          {t('admin.rSimManufacturingFile.dragAndDropFileOr')}
                        </Typography>

                        <label htmlFor="file-input">
                          <Input
                            id="file-input"
                            name="rSimManufacturingFile file input"
                            data-testid="rSimManufacturingFile file input"
                            inputProps={{
                              'data-testid': 'rSimManufacturingFile file input element',
                            }}
                            type="file"
                            sx={{ display: 'none' }}
                            onChange={(event) => {
                              const fileInput = event.target as HTMLInputElement;
                              if (fileInput?.files?.[0]) {
                                selectFile(fileInput.files[0]);
                                // file input cannot be controlled. This is hack to reset the input field that allow
                                // upload the same file in row
                                event.target.value = '';
                              }
                            }}
                          />
                          <Button
                            data-testid="rSimManufacturingFile browse button"
                            variant="contained"
                            component="span"
                            sx={{
                              px: 16,
                              py: 10,
                            }}
                          >
                            {t('admin.rSimManufacturingFile.browse')}
                          </Button>
                        </label>
                      </>
                    )}
                    {/* ------------- PLIK WYBRANY + LOADING ---------------- */}
                    {selectedFile && uploading && (
                      <Button
                        data-testid="rSimManufacturingFile uploading button"
                        variant="contained"
                        component="span"
                        sx={{
                          px: 40,
                          py: 10,
                        }}
                      >
                        <AutorenewOutlinedIcon fontSize="small" />
                      </Button>
                    )}
                    {selectedFile && !uploading && !importId && (
                      <>
                        <TransparentButtonStyled
                          variant="text"
                          color="tertiary"
                          data-testid="rSimManufacturingFile clear button"
                          onClick={() => clear()}
                        >
                          {t('admin.rSimManufacturingFile.clear')}
                        </TransparentButtonStyled>
                        <ButtonStyled
                          data-testid="rSimManufacturingFile upload button"
                          variant="contained"
                          startIcon={<PublishOutlinedIcon />}
                          onClick={() => uploadFile()}
                          disabled={!!importStatus?.messages}
                        >
                          {t('admin.rSimManufacturingFile.upload')}
                        </ButtonStyled>
                      </>
                    )}
                    {selectedFile && !uploading && importId && (
                      <>
                        <TransparentButtonStyled
                          variant="text"
                          color="tertiary"
                          data-testid="rSimManufacturingFile upload new file button"
                          onClick={() => clear()}
                        >
                          {t('admin.rSimManufacturingFile.uploadNewFile')}
                        </TransparentButtonStyled>
                        <Button
                          data-testid="rSimManufacturingFile download results button"
                          variant="contained"
                          startIcon={<DownloadOutlinedIcon />}
                          onClick={() => downloadOutput()}
                        >
                          {t('admin.rSimManufacturingFile.downloadResults')}
                        </Button>
                      </>
                    )}
                  </ButtonBox>
                </BoxStyled>
              </Container>
              {dropAreaDisplayed && (
                <DropAreaBox>
                  <Typography variant="h1" color="text.secondary">
                    {t('common.dropFile')}
                  </Typography>
                </DropAreaBox>
              )}

              <Stack>
                <AutoHideSnackbar
                  open={!!importStatus?.title}
                  severity={importStatus?.severity || 'error'}
                  onClose={() => {
                    setImportStatus((prev) => {
                      return prev ? { ...prev, title: '' } : null;
                    });
                  }}
                >
                  <AlertTitle>{importStatus?.title || ''}</AlertTitle>
                  {importStatus?.description && (
                    <Typography variant="text1" color="text.white">
                      {importStatus?.description}
                    </Typography>
                  )}
                </AutoHideSnackbar>
              </Stack>
            </Box>
          );
        }}
      </Dropzone>
    </>
  );
};

export const RSimManufacturingFilePage = () => {
  const { t } = useTranslation();
  return (
    <TabPanelWithErrorHandling name={t('admin.rSimManufacturingFile.rSimManufacturingFile')}>
      <RSimManufacturingFilePageContent />
    </TabPanelWithErrorHandling>
  );
};
